/**
 * WorkflowAutomations -- Scheduled automations view.
 *
 * Shows a compact 7-day calendar strip of upcoming scheduled tasks and a
 * card grid of all scheduled automations with status, schedule, and agent info.
 */

import { useState, useMemo } from "react";
import { useCronJobs, useWorkflowAgents } from "./useWorkflowQueries";
import type { CronJob } from "./types";
import { WorkflowAutomationCard } from "./WorkflowAutomationCard";
import styles from "./workflow.module.scss";

// -- Helpers -----------------------------------------------------------------

/** Generate the next 7 days starting from today. */
function buildNext7Days(): Date[] {
  const days: Date[] = [];
  const now = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    d.setHours(0, 0, 0, 0);
    days.push(d);
  }
  return days;
}

const DAY_NAMES_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

// -- Component ---------------------------------------------------------------

const WorkflowAutomations = () => {
  const { data: cronData, isLoading, error } = useCronJobs();
  const { data: agents } = useWorkflowAgents();
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const jobs: CronJob[] = useMemo(() => cronData?.jobs ?? [], [cronData?.jobs]);
  const next7Days = useMemo(() => buildNext7Days(), []);

  // Build a map of day -> jobs that run on that day (based on next_run)
  const jobsByDay = useMemo(() => {
    const map = new Map<string, CronJob[]>();
    for (const day of next7Days) {
      const key = day.toISOString().slice(0, 10);
      map.set(key, []);
    }
    for (const job of jobs) {
      if (!job.next_run) continue;
      const nextRunDate = new Date(job.next_run);
      const key = nextRunDate.toISOString().slice(0, 10);
      const existing = map.get(key);
      if (existing) {
        existing.push(job);
      }
    }
    return map;
  }, [jobs, next7Days]);

  // Filter jobs when a day is selected
  const displayJobs = useMemo(() => {
    if (!selectedDay) return jobs;
    const key = selectedDay.toISOString().slice(0, 10);
    return jobsByDay.get(key) ?? [];
  }, [selectedDay, jobs, jobsByDay]);

  // Agent lookup
  const agentMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const agent of agents ?? []) {
      map.set(agent.id, agent.name);
    }
    return map;
  }, [agents]);

  const activeCount = jobs.filter(j => j.enabled).length;

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <span>Loading automations...</span>
      </div>
    );
  }

  if (error) {
    return <div className={styles.errorState}>Failed to load automations: {String(error)}</div>;
  }

  // Full empty state when no jobs exist at all
  if (jobs.length === 0) {
    return (
      <div className={styles.automationsPage}>
        <div className={styles.automationsEmptyHero}>
          <div className={styles.automationsEmptyIcon}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <h3 className={styles.automationsEmptyTitle}>No automations configured yet</h3>
          <p className={styles.automationsEmptyDescription}>
            Cron jobs will appear here once synced from the agent. Scheduled automations let you run
            recurring tasks on a defined cadence.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.automationsPage}>
      {/* Summary stats */}
      <div className={styles.automationsSummary}>
        <div className={styles.automationsSummaryCard} data-testid="summary-total-card">
          <span className={styles.automationsSummaryValue}>{jobs.length}</span>
          <span className={styles.automationsSummaryLabel}>Total</span>
        </div>
        <div className={`${styles.automationsSummaryCard} ${styles.automationsSummaryActive}`}>
          <span className={styles.automationsSummaryValue}>{activeCount}</span>
          <span className={styles.automationsSummaryLabel}>Active</span>
        </div>
        <div className={`${styles.automationsSummaryCard} ${styles.automationsSummaryDisabled}`}>
          <span className={styles.automationsSummaryValue}>{jobs.length - activeCount}</span>
          <span className={styles.automationsSummaryLabel}>Disabled</span>
        </div>
      </div>

      {/* 7-Day Calendar Strip */}
      <div className={styles.calendarStrip}>
        <div className={styles.calendarStripHeader}>
          <h3 className={styles.calendarStripTitle}>Upcoming Schedule</h3>
          {selectedDay && (
            <button
              type="button"
              className={styles.calendarClearFilter}
              onClick={() => setSelectedDay(null)}
            >
              Show all
            </button>
          )}
        </div>
        <div className={styles.calendarDays}>
          {next7Days.map(day => {
            const key = day.toISOString().slice(0, 10);
            const dayJobs = jobsByDay.get(key) ?? [];
            const isSelected = selectedDay ? isSameDay(selectedDay, day) : false;
            const dayIsToday = isToday(day);

            return (
              <button
                key={key}
                type="button"
                className={`${styles.calendarDay} ${isSelected ? styles.calendarDaySelected : ""} ${dayIsToday ? styles.calendarDayToday : ""}`}
                onClick={() => setSelectedDay(isSelected ? null : day)}
              >
                <span className={styles.calendarDayName}>{DAY_NAMES_SHORT[day.getDay()]}</span>
                <span className={styles.calendarDayNumber}>{day.getDate()}</span>
                <div className={styles.calendarDayDots}>
                  {dayJobs.length === 0 ? (
                    <span className={styles.calendarDotEmpty}>--</span>
                  ) : (
                    <>
                      {dayJobs.slice(0, 4).map(job => (
                        <span
                          key={job.id}
                          className={`${styles.calendarDot} ${job.enabled ? styles.calendarDotActive : styles.calendarDotDisabled}`}
                          title={job.name}
                        />
                      ))}
                      {dayJobs.length > 4 && (
                        <span className={styles.calendarDotMore}>+{dayJobs.length - 4}</span>
                      )}
                    </>
                  )}
                </div>
                <span className={styles.calendarDayCount}>
                  {dayJobs.length} {dayJobs.length === 1 ? "task" : "tasks"}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Automations Card Grid */}
      <div className={styles.automationsList}>
        <div className={styles.automationsListHeader}>
          <h3 className={styles.automationsListTitle}>
            {selectedDay
              ? `Showing ${displayJobs.length} for ${selectedDay.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
              : "All Automations"}
          </h3>
          <span className={styles.automationsCount}>
            {displayJobs.length} {displayJobs.length === 1 ? "automation" : "automations"}
          </span>
        </div>

        {displayJobs.length === 0 ? (
          <div className={styles.emptyState}>
            <span>No automations scheduled for this day.</span>
          </div>
        ) : (
          <div className={styles.automationsCards}>
            {displayJobs.map(job => (
              <WorkflowAutomationCard key={job.id} job={job} agentMap={agentMap} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowAutomations;
